import { useState, useRef, useEffect, memo, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { formatCurrency, formatDate } from '../utils/calculations';
import { deleteTransaction, updateTransaction, restoreTransaction } from '../utils/api';
import { showSuccess, showError, showWarning } from '../utils/notifications';
import useTransactionStore from '../stores/useTransactionStore';
import BulkEditModal from './BulkEditModal';
import TableRowDetail from './TableRowDetail';
import TableFilters from './TableFilters';
import Icon from './icons/IconLibrary';
import { handleSwipeGesture, isTouchDevice, createSwipeableListItem } from '../utils/touchUtils';

// Phase 48.5: Memoize expensive table component to prevent unnecessary re-renders
function TransactionTable({ transactions: propTransactions, onUpdate }) {
  // Use store if available, otherwise fall back to props
  const storeTransactions = useTransactionStore((state) => state.transactions);
  const transactions = propTransactions || storeTransactions;
  
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [isTouch, setIsTouch] = useState(false);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [filterPresets, setFilterPresets] = useState([]);
  
  // Refs for virtualization
  const parentRef = useRef(null);

  // Load accounts for bulk edit modal, detect touch device, and load filter presets
  useEffect(() => {
    fetchAccounts();
    setIsTouch(isTouchDevice());
    loadFilterPresets();
  }, []);

  const loadFilterPresets = () => {
    try {
      const saved = localStorage.getItem('transactionFilterPresets');
      if (saved) {
        setFilterPresets(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading filter presets:', error);
    }
  };

  const saveFilterPreset = (preset) => {
    const newPresets = [...filterPresets, preset];
    setFilterPresets(newPresets);
    localStorage.setItem('transactionFilterPresets', JSON.stringify(newPresets));
    showSuccess(`Filtro "${preset.name}" guardado`);
  };

  const fetchAccounts = async () => {
    try {
      const response = await fetch('/api/accounts', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const handleDelete = async (id) => {
    if (confirm('¿Estás seguro de eliminar esta transacción? (Se puede restaurar después)')) {
      try {
        await deleteTransaction(id);
        showSuccess('Transacción eliminada exitosamente (soft delete)');
        if (onUpdate) onUpdate();
      } catch (error) {
        showError(`Error al eliminar: ${error.message}`);
      }
    }
  };

  const handleRestore = async (id) => {
    try {
      await restoreTransaction(id);
      showSuccess('Transacción restaurada exitosamente');
      if (onUpdate) onUpdate();
    } catch (error) {
      showError(`Error al restaurar: ${error.message}`);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    if (confirm(`¿Estás seguro de eliminar ${selectedIds.length} transacciones?`)) {
      try {
        await Promise.all(selectedIds.map(id => deleteTransaction(id)));
        setSelectedIds([]);
        showSuccess(`${selectedIds.length} transacciones eliminadas exitosamente`);
        if (onUpdate) onUpdate();
      } catch (error) {
        showError(`Error al eliminar: ${error.message}`);
      }
    }
  };

  const handleBulkCategoryChange = async (newCategory) => {
    if (selectedIds.length === 0) return;
    
    try {
      await Promise.all(
        selectedIds.map(id => {
          const transaction = transactions.find(t => t.id === id);
          return updateTransaction(id, { ...transaction, category: newCategory });
        })
      );
      setSelectedIds([]);
      showSuccess(`${selectedIds.length} transacciones actualizadas a categoría ${newCategory}`);
      if (onUpdate) onUpdate();
    } catch (error) {
      showError(`Error al actualizar: ${error.message}`);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === transactions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(transactions.map(t => t.id));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const startEdit = (transaction) => {
    setEditingId(transaction.id);
    setEditForm({
      date: transaction.date,
      description: transaction.description,
      amount: transaction.amount,
      type: transaction.type,
      category: transaction.category,
      account: transaction.account || '',
      is_deductible: transaction.is_deductible ? 1 : 0,
      transaction_type: transaction.transaction_type || 'personal',
      category_id: transaction.category_id || null,
      linked_invoice_id: transaction.linked_invoice_id || null,
      notes: transaction.notes || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    try {
      await updateTransaction(editingId, editForm);
      setEditingId(null);
      setEditForm({});
      showSuccess('Transacción actualizada exitosamente');
      if (onUpdate) onUpdate();
    } catch (error) {
      showError(`Error al actualizar: ${error.message}`);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortedTransactions = () => {
    // First filter
    let filtered = [...transactions];
    
    // Apply filters
    if (filters.type) {
      filtered = filtered.filter(t => t.type === filters.type);
    }
    if (filters.category) {
      filtered = filtered.filter(t => t.category === filters.category);
    }
    if (filters.transaction_type) {
      filtered = filtered.filter(t => t.transaction_type === filters.transaction_type);
    }
    if (filters.is_deductible) {
      filtered = filtered.filter(t => t.is_deductible === true);
    }
    if (filters.description) {
      filtered = filtered.filter(t => 
        t.description.toLowerCase().includes(filters.description.toLowerCase())
      );
    }
    if (filters.daterange?.start) {
      filtered = filtered.filter(t => t.date >= filters.daterange.start);
    }
    if (filters.daterange?.end) {
      filtered = filtered.filter(t => t.date <= filters.daterange.end);
    }
    if (filters.amountrange?.min !== null && filters.amountrange?.min !== undefined) {
      filtered = filtered.filter(t => parseFloat(t.amount) >= filters.amountrange.min);
    }
    if (filters.amountrange?.max !== null && filters.amountrange?.max !== undefined) {
      filtered = filtered.filter(t => parseFloat(t.amount) <= filters.amountrange.max);
    }

    // Then sort
    const sorted = filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'amount') {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    return sorted;
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <Icon name="chevron-up-down" className="w-4 h-4 text-gray-400 inline ml-1" />;
    }
    return sortOrder === 'asc' 
      ? <Icon name="chevron-up" className="w-4 h-4 inline ml-1" />
      : <Icon name="chevron-down" className="w-4 h-4 inline ml-1" />;
  };

  const toggleRowExpand = (transactionId) => {
    if (expandedRowId === transactionId) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(transactionId);
    }
  };

  if (!transactions || transactions.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-md text-center text-gray-500 dark:text-gray-400">
        No hay transacciones
      </div>
    );
  }

  const sortedTransactions = getSortedTransactions();

  // Setup virtualizer for large lists
  const rowVirtualizer = useVirtualizer({
    count: sortedTransactions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // Estimated row height
    overscan: 10 // Number of items to render outside of viewport
  });

  // Available filter configurations
  const availableFilters = [
    {
      key: 'description',
      type: 'text',
      label: 'Descripción',
      placeholder: 'Buscar en descripción...'
    },
    {
      key: 'type',
      type: 'select',
      label: 'Tipo',
      options: [
        { value: 'ingreso', label: 'Ingreso' },
        { value: 'gasto', label: 'Gasto' }
      ]
    },
    {
      key: 'category',
      type: 'select',
      label: 'Categoría',
      options: [
        { value: 'personal', label: 'Personal' },
        { value: 'avanta', label: 'Avanta' }
      ]
    },
    {
      key: 'transaction_type',
      type: 'select',
      label: 'Clasificación',
      options: [
        { value: 'personal', label: 'Personal' },
        { value: 'business', label: 'Negocio' },
        { value: 'transfer', label: 'Transferencia' }
      ]
    },
    {
      key: 'daterange',
      type: 'daterange',
      label: 'Rango de fechas'
    },
    {
      key: 'amountrange',
      type: 'numberrange',
      label: 'Rango de monto'
    },
    {
      key: 'is_deductible',
      type: 'boolean',
      label: 'Solo deducibles'
    }
  ];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <TableFilters
        availableFilters={availableFilters}
        initialFilters={filters}
        onFiltersChange={setFilters}
        presets={filterPresets}
        onSavePreset={saveFilterPreset}
        onClearFilters={() => setFilters({})}
        showSavePreset={true}
      />

      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-md overflow-hidden">
      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 border-b border-blue-200 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedIds.length} transacción(es) seleccionada(s)
          </span>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowBulkEditModal(true)}
              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
            >
              ✏️ Editar
            </button>
            <button
              onClick={() => handleBulkCategoryChange('personal')}
              className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
            >
              → Personal
            </button>
            <button
              onClick={() => handleBulkCategoryChange('avanta')}
              className="px-3 py-1 bg-blue-600 dark:bg-blue-700 text-white text-sm rounded hover:bg-blue-700 dark:hover:bg-blue-600"
            >
              → Avanta
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1 bg-red-600 dark:bg-red-700 text-white text-sm rounded hover:bg-red-700"
            >
              Eliminar
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="px-3 py-1 bg-gray-300 text-gray-700 dark:text-gray-300 text-sm rounded hover:bg-gray-400"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={showBulkEditModal}
        onClose={() => {
          setShowBulkEditModal(false);
          setSelectedIds([]);
        }}
        selectedTransactions={transactions.filter(t => selectedIds.includes(t.id))}
        onUpdate={() => {
          if (onUpdate) onUpdate();
        }}
        accounts={accounts}
      />

      {/* Desktop Table View - hidden on mobile */}
      <div className="hidden md:block overflow-x-auto">
        <div 
          ref={parentRef}
          className="overflow-y-auto"
          style={{ maxHeight: '600px' }}
        >
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === transactions.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('date')}
                >
                  Fecha <SortIcon column="date" />
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('description')}
                >
                  Descripción <SortIcon column="description" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Categoría</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Clasificación</th>
                <th 
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('amount')}
                >
                  Monto <SortIcon column="amount" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Deducible</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const transaction = sortedTransactions[virtualRow.index];
                return (
                  <>
                    <tr 
                      key={transaction.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-slate-800 ${selectedIds.includes(transaction.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                    >
                <td className="px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(transaction.id)}
                    onChange={() => handleSelectOne(transaction.id)}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                </td>
                
                {editingId === transaction.id ? (
                  // Edit mode
                  <>
                    <td className="px-4 py-3 text-sm">
                      <input
                        type="date"
                        value={editForm.date}
                        onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                        className="border rounded px-2 py-1 w-full"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <input
                        type="text"
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="border rounded px-2 py-1 w-full"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <select
                        value={editForm.type}
                        onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                        className="border rounded px-2 py-1 w-full"
                      >
                        <option value="ingreso">ingreso</option>
                        <option value="gasto">gasto</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <select
                        value={editForm.category}
                        onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                        className="border rounded px-2 py-1 w-full"
                      >
                        <option value="personal">personal</option>
                        <option value="avanta">avanta</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <select
                        value={editForm.transaction_type || 'personal'}
                        onChange={(e) => setEditForm({ ...editForm, transaction_type: e.target.value })}
                        className="border rounded px-2 py-1 w-full text-xs"
                      >
                        <option value="personal">👤 Personal</option>
                        <option value="business">💼 Negocio</option>
                        <option value="transfer">🔄 Transfer</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.amount}
                        onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) })}
                        className="border rounded px-2 py-1 w-full text-right"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <input
                        type="checkbox"
                        checked={editForm.is_deductible === 1}
                        onChange={(e) => setEditForm({ ...editForm, is_deductible: e.target.checked ? 1 : 0 })}
                        className="rounded border-gray-300 dark:border-slate-600"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex gap-1 justify-center">
                        <button
                          onClick={saveEdit}
                          className="text-green-600 hover:text-green-900 px-2"
                        >
                          ✓
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 px-2"
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  // View mode
                  <>
                    <td className="px-4 py-3 text-sm">{formatDate(transaction.date)}</td>
                    <td className="px-4 py-3 text-sm">{transaction.description}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        transaction.type === 'ingreso' 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                      }`}>
                        {transaction.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        transaction.category === 'avanta' 
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {transaction.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        transaction.transaction_type === 'business' 
                          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' 
                          : transaction.transaction_type === 'transfer'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {transaction.transaction_type === 'business' ? '💼 Negocio' : 
                         transaction.transaction_type === 'transfer' ? '🔄 Transfer' : 
                         '👤 Personal'}
                      </span>
                      {transaction.linked_invoice_id && (
                        <span className="ml-1 text-xs" title="Factura vinculada">📄</span>
                      )}
                      {transaction.notes && (
                        <span className="ml-1 text-xs" title={transaction.notes}>📝</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-medium ${
                      transaction.type === 'ingreso' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      {/* Phase 16: Granular Tax Deductibility Indicators */}
                      <div className="flex gap-1 justify-center items-center">
                        {transaction.is_isr_deductible && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" title="Deducible ISR">
                            ISR
                          </span>
                        )}
                        {transaction.is_iva_deductible && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" title="IVA Acreditable">
                            IVA
                          </span>
                        )}
                        {!transaction.is_isr_deductible && !transaction.is_iva_deductible && transaction.is_deductible && (
                          <span className="text-gray-400" title="Deducible (legacy)">✓</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex gap-1 justify-center">
                        <button
                          onClick={() => toggleRowExpand(transaction.id)}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 px-2"
                          title="Ver detalles"
                        >
                          <Icon 
                            name={expandedRowId === transaction.id ? "chevron-up" : "chevron-down"} 
                            className="w-4 h-4" 
                          />
                        </button>
                        <button
                          onClick={() => startEdit(transaction)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 px-2"
                          title="Editar"
                        >
                          <Icon name="pencil" className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(transaction.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 px-2"
                          title="Eliminar"
                        >
                          <Icon name="trash" className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                )}
                    </tr>
                    
                    {/* Expandable Detail Row */}
                    {expandedRowId === transaction.id && (
                      <tr key={`${transaction.id}-detail`}>
                        <td colSpan="9" className="p-0">
                          <TableRowDetail 
                            transaction={transaction} 
                            onClose={() => setExpandedRowId(null)} 
                          />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View - visible only on mobile */}
      <div className="md:hidden">
        {sortedTransactions.map((transaction) => (
          <div 
            key={transaction.id}
            className={`border-b border-gray-200 dark:border-slate-700 p-4 ${selectedIds.includes(transaction.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
          >
            {editingId === transaction.id ? (
              // Mobile Edit Mode
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Fecha</label>
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    className="border rounded px-2 py-1 w-full mt-1 dark:bg-slate-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Descripción</label>
                  <input
                    type="text"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="border rounded px-2 py-1 w-full mt-1 dark:bg-slate-800 dark:text-gray-100"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Tipo</label>
                    <select
                      value={editForm.type}
                      onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                      className="border rounded px-2 py-1 w-full mt-1 dark:bg-slate-800 dark:text-gray-100"
                    >
                      <option value="ingreso">ingreso</option>
                      <option value="gasto">gasto</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Categoría</label>
                    <select
                      value={editForm.category}
                      onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                      className="border rounded px-2 py-1 w-full mt-1 dark:bg-slate-800 dark:text-gray-100"
                    >
                      <option value="personal">personal</option>
                      <option value="avanta">avanta</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Monto</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.amount}
                      onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) })}
                      className="border rounded px-2 py-1 w-full mt-1 dark:bg-slate-800 dark:text-gray-100"
                    />
                  </div>
                  <div className="flex items-center mt-5">
                    <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={editForm.is_deductible === 1}
                        onChange={(e) => setEditForm({ ...editForm, is_deductible: e.target.checked ? 1 : 0 })}
                        className="rounded border-gray-300 dark:border-slate-600 mr-2"
                      />
                      Deducible
                    </label>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={saveEdit}
                    className="flex-1 bg-green-600 dark:bg-green-700 text-white px-4 py-2 rounded hover:bg-green-700 dark:hover:bg-green-600"
                  >
                    Guardar
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="flex-1 bg-gray-300 dark:bg-slate-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded hover:bg-gray-400 dark:hover:bg-slate-500"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              // Mobile View Mode
              <div>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(transaction.id)}
                      onChange={() => handleSelectOne(transaction.id)}
                      className="rounded border-gray-300 dark:border-slate-600"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">{transaction.description}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{formatDate(transaction.date)}</div>
                    </div>
                  </div>
                  <div className={`text-lg font-bold ${
                    transaction.type === 'ingreso' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(transaction.amount)}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    transaction.type === 'ingreso' 
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }`}>
                    {transaction.type}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    transaction.category === 'avanta' 
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {transaction.category}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    transaction.transaction_type === 'business' 
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' 
                      : transaction.transaction_type === 'transfer'
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {transaction.transaction_type === 'business' ? '💼 Negocio' : 
                     transaction.transaction_type === 'transfer' ? '🔄 Transfer' : 
                     '👤 Personal'}
                  </span>
                  {/* Phase 16: Granular Tax Deductibility Badges */}
                  {transaction.is_isr_deductible && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                      ISR Deducible
                    </span>
                  )}
                  {transaction.is_iva_deductible && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                      IVA Acreditable
                    </span>
                  )}
                  {transaction.expense_type && transaction.expense_type !== 'national' && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">
                      {transaction.expense_type === 'international_with_invoice' ? '🌍 Intl. c/Factura' : '🌍 Intl. s/Factura'}
                    </span>
                  )}
                  {!transaction.is_isr_deductible && !transaction.is_iva_deductible && transaction.is_deductible && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                      Deducible (legacy)
                    </span>
                  )}
                  {transaction.linked_invoice_id && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-800">
                      📄 Factura
                    </span>
                  )}
                  {transaction.notes && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800" title={transaction.notes}>
                      📝 Notas
                    </span>
                  )}
                </div>
                
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => toggleRowExpand(transaction.id)}
                    className="flex-1 bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded text-sm hover:bg-gray-100 dark:hover:bg-slate-600 flex items-center justify-center gap-1"
                  >
                    <Icon name={expandedRowId === transaction.id ? "chevron-up" : "chevron-down"} className="w-4 h-4" />
                    {expandedRowId === transaction.id ? 'Ocultar' : 'Detalles'}
                  </button>
                  <button
                    onClick={() => startEdit(transaction)}
                    className="flex-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-3 py-1 rounded text-sm hover:bg-blue-100 dark:hover:bg-blue-900/30 flex items-center justify-center gap-1"
                  >
                    <Icon name="pencil" className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(transaction.id)}
                    className="flex-1 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-3 py-1 rounded text-sm hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center gap-1"
                  >
                    <Icon name="trash" className="w-4 h-4" />
                    Eliminar
                  </button>
                </div>
              </div>
            )}
            
            {/* Mobile Detail View */}
            {expandedRowId === transaction.id && editingId !== transaction.id && (
              <div className="mt-3">
                <TableRowDetail 
                  transaction={transaction} 
                  onClose={() => setExpandedRowId(null)} 
                />
              </div>
            )}
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}

// Phase 48.5: Export memoized component for better performance
export default memo(TransactionTable);
